<template>
  <section class="estimate-document-viewer" aria-labelledby="estimate-document-viewer-title">
    <div class="viewer-header">
      <div>
        <h2 id="estimate-document-viewer-title">見積書</h2>
        <p>保存されている内容を、出力される見積書と同じ形式で表示しています。</p>
      </div>
      <div class="viewer-meta" aria-label="帳票設定">
        <span>{{ layoutLabel }}</span>
        <span>{{ languageLabel }}</span>
      </div>
    </div>

    <div
      class="viewer-scroll"
      :class="{ 'viewer-scroll--landscape': isLandscape }"
      tabindex="0"
      aria-label="見積書の表示領域"
    >
      <div class="viewer-document">
        <component :is="documentComponent" :estimate="estimate" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import EstimateLandscapeDetailedPdf from '@/components/estimates/EstimateLandscapeDetailedPdf.vue';
import EstimatePortraitAnnualPdf from '@/components/estimates/EstimatePortraitAnnualPdf.vue';
import EstimatePortraitSimplePdf from '@/components/estimates/EstimatePortraitSimplePdf.vue';
import EstimatePortraitSimplePdfEn from '@/components/estimates/EstimatePortraitSimplePdfEn.vue';
import EstimatePortraitTemporaryPdf from '@/components/estimates/EstimatePortraitTemporaryPdf.vue';

const props = defineProps({
  estimate: {
    type: Object,
    required: true
  }
});

const documentComponent = computed(() => {
  const components = {
    portrait_temporary: EstimatePortraitTemporaryPdf,
    portrait_annual: EstimatePortraitAnnualPdf,
    landscape_detailed: EstimateLandscapeDetailedPdf
  };

  return components[props.estimate.output_layout_type] || (
    props.estimate.default_language_type === 'english'
      ? EstimatePortraitSimplePdfEn
      : EstimatePortraitSimplePdf
  );
});

const isLandscape = computed(() =>
  props.estimate.output_layout_type === 'landscape_detailed'
);

const layoutLabel = computed(() => ({
  portrait_simple: '縦型・簡易（旧形式）',
  portrait_temporary: '縦型・臨時',
  portrait_annual: '縦型・年間',
  landscape_detailed: '横型・詳細'
})[props.estimate.output_layout_type] || props.estimate.output_layout_type || '-');

const languageLabel = computed(() =>
  props.estimate.default_language_type === 'english' ? '英語' : '日本語'
);
</script>

<style scoped>
.estimate-document-viewer {
  margin-bottom: 24px;
  overflow: hidden;
  border: 1px solid #cbd3da;
  border-radius: 6px;
  background: #fff;
}

.viewer-header {
  display: flex;
  min-height: 70px;
  padding: 14px 18px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #cbd3da;
  background: #f8f9fa;
}

.viewer-header h2 {
  margin: 0;
  font-size: 20px;
}

.viewer-header p {
  margin: 4px 0 0;
  color: #6c757d;
  font-size: 13px;
}

.viewer-meta {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.viewer-meta span {
  padding: 4px 9px;
  border-radius: 999px;
  background: #e4ebf1;
  color: #243746;
  font-size: 12px;
  font-weight: 700;
}

.viewer-scroll {
  padding: 16px;
  overflow-x: auto;
  background: #f5f6f7;
  scrollbar-gutter: stable;
}

.viewer-scroll:focus-visible {
  outline: 3px solid #80bdff;
  outline-offset: -3px;
}

.viewer-document {
  width: 210mm;
  margin: 0 auto;
  background: #fff;
  box-shadow: 0 1px 4px rgba(21, 39, 52, 0.08);
}

/* 詳細画面共通の白文字指定を帳票内では解除する。 */
.viewer-document :deep(th) {
  color: #111;
}

/* 詳細画面の共通テーブル見出し色を、横型見積書の金額欄へ持ち込まない。 */
.viewer-document :deep(.cover-amount-table th) {
  background: #fff;
}

.viewer-scroll--landscape .viewer-document {
  width: 297mm;
}

.viewer-document :deep([data-estimate-pdf-page] + [data-estimate-pdf-page]) {
  margin-top: 20px;
}

@media (max-width: 767px) {
  .viewer-header {
    padding: 12px;
    align-items: flex-start;
    flex-direction: column;
  }

  .viewer-meta {
    justify-content: flex-start;
  }

  .viewer-scroll {
    padding: 12px;
  }
}
</style>
